// src/pages/Admin/ClientLocator.js
//
// Client Locator — unified map + search lookup over commercial and residential
// clients. Pick a client (by tapping a map pin or via the unified search) and a
// date; the panel shows every job for that client on that day, in ANY status.
//
// Backend: /api/client-locator/{sites,search,client/:type/:id/jobs}
//   • Commercial sites carry lat/lng → real map pins, coloured by that day's job status.
//   • Residential customers have no coordinates → searchable + date lookup, no pin.
//
// Stack already in the project: react-leaflet@5, leaflet, framer-motion,
// react-datepicker, date-fns, lucide-react. No new dependencies.

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Building2, Home, Globe2, Map as MapIcon, Layers, Users, Clock,
  X, Loader2, Briefcase, ChevronRight, Phone, Navigation, CalendarDays,
  MapPin, Crosshair, Inbox,
} from 'lucide-react';

const API = process.env.REACT_APP_API_BASE_URL; // already includes /api

// ── Palette ───────────────────────────────────────────────────────────────
const C = {
  bg0: '#0B1120', bg1: '#0F172A',
  surface: 'rgba(255,255,255,0.045)', surface2: 'rgba(255,255,255,0.07)',
  border: 'rgba(255,255,255,0.09)', borderHi: 'rgba(56,189,248,0.45)',
  text: '#E6EDF6', muted: '#93A3B8', faint: '#64748B',
  accent: '#38BDF8', accent2: '#6366F1',
};

// ── Job-status → label + colour ─────────────────────────────────────────────
const STATUS = {
  scheduled:     { label: 'Scheduled',     color: '#38BDF8' },
  pending:       { label: 'Pending',       color: '#38BDF8' },
  in_progress:   { label: 'In Progress',   color: '#F59E0B' },
  started:       { label: 'Started',       color: '#F59E0B' },
  before_photos: { label: 'Before Photos', color: '#A78BFA' },
  after_photos:  { label: 'After Photos',  color: '#A78BFA' },
  completed:     { label: 'Completed',     color: '#34D399' },
  cancelled:     { label: 'Cancelled',     color: '#F87171' },
  rescheduled:   { label: 'Rescheduled',   color: '#FB923C' },
};
const NO_JOB = { label: 'No job', color: '#64748B' };
const getSiteInfo = (s) => {
  if (s && s.job_status) {
    return STATUS[String(s.job_status).toLowerCase()] || { label: s.job_status, color: '#94A3B8' };
  }
  if (s && s.type === 'residential') return { label: 'Residential', color: '#10B981' };
  return NO_JOB;
};

// ── Leaflet pin (divIcon so we can pulse it with CSS) ───────────────────────
const pinIcon = (color, active = false, type = 'commercial') =>
  L.divIcon({
    className: 'cl-pin-wrap',
    html: `<span class="cl-pin cl-pin-${type} ${active ? 'cl-pin-active' : ''}" style="--pin:${color}"></span>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    popupAnchor: [0, -10],
  });

// ── Imperative map controller (flyTo / fitBounds on demand) ─────────────────
function MapController({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    if (target.type === 'bounds' && target.points && target.points.length) {
      try {
        map.flyToBounds(L.latLngBounds(target.points), {
          padding: [70, 70], maxZoom: 15, duration: 0.9,
        });
      } catch (_) { /* ignore degenerate bounds */ }
    } else if (target.type === 'point') {
      map.flyTo(target.center, target.zoom ?? 14, { duration: 0.9 });
    }
  }, [target, map]);
  return null;
}

// ── Themed date-picker trigger ──────────────────────────────────────────────
const DateInput = React.forwardRef(({ value, onClick }, ref) => (
  <button type="button" className="cl-date-btn" onClick={onClick} ref={ref}>
    <CalendarDays size={16} />
    <span>{value}</span>
  </button>
));
DateInput.displayName = 'DateInput';

// ── Animation variants ──────────────────────────────────────────────────────
const rise = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 260, damping: 24 } },
};

const TIERS = [
  { key: 'country', label: 'Country', Icon: Globe2 },
  { key: 'region',  label: 'Region',  Icon: Layers },
  { key: 'city',    label: 'City',    Icon: MapIcon },
];

export default function ClientLocator() {
  const [date, setDate] = useState(new Date());
  const dateStr = useMemo(() => format(date, 'yyyy-MM-dd'), [date]);

  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [allCommercial, setAllCommercial] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const [selected, setSelected] = useState(null); // { type, id, name, sub, mapped }
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  const [tier, setTier] = useState('country');
  const [activeRegion, setActiveRegion] = useState(null);
  const [activeCity, setActiveCity] = useState(null);
  const [clientTypeFilter, setClientTypeFilter] = useState('all');

  const [target, setTarget] = useState(null);
  const didInitialFit = useRef(false);
  const searchBoxRef = useRef(null);

  // ── fit helper ────────────────────────────────────────────────────────────
  const fitToSites = useCallback((arr) => {
    const points = arr
      .filter((s) => s.latitude != null && s.longitude != null)
      .map((s) => [s.latitude, s.longitude]);
    if (points.length === 1) {
      setTarget({ type: 'point', center: points[0], zoom: 14, token: Date.now() });
    } else if (points.length > 1) {
      setTarget({ type: 'bounds', points, token: Date.now() });
    }
  }, []);

  // ── data: sites (re-fetched when the date changes to recolour pins) ─────────
  const fetchSites = useCallback(async (d) => {
    setLoadingSites(true);
    try {
      const [rComm, rResi] = await Promise.all([
        axios.get(`${API}/client-locator/sites`, { params: { date: format(d, 'yyyy-MM-dd'), limit: 10000 } }),
        axios.get(`${API}/customers`, { params: { limit: 10000 } }).catch(() => ({ data: { data: [] } }))
      ]);

      const commSites = rComm.data?.sites || [];
      const mappedComm = commSites.map(s => ({ ...s, type: 'commercial' }));

      const resiCusts = rResi.data?.data || [];
      const mappedResi = resiCusts.map(c => {
        let lat = null, lon = null;
        if (c.pin_location) {
          const m = c.pin_location.match(/^([-\d.]+),\s*([-\d.]+)$/);
          if (m) { lat = parseFloat(m[1]); lon = parseFloat(m[2]); }
        }
        return {
          site_id: `resi-${c.Cust_id}`,
          site_name: c.client_name,
          address: c.address,
          city: c.client_city,
          state: c.client_state,
          latitude: lat,
          longitude: lon,
          client_id: c.Cust_id,
          client_name: c.client_name,
          job_status: null,
          type: 'residential'
        };
      }).filter(s => s.latitude != null && s.longitude != null);

      const allData = [...mappedComm, ...mappedResi];
      setSites(allData);
      if (!didInitialFit.current && allData.length) {
        fitToSites(allData);
        didInitialFit.current = true;
      }
    } catch (e) {
      setSites([]);
    } finally {
      setLoadingSites(false);
    }
  }, [fitToSites]);

  // ── data: jobs for a client on the current date ─────────────────────────────
  const fetchJobs = useCallback(async (item, d) => {
    if (!item) return;
    setLoadingJobs(true);
    try {
      const r = await axios.get(
        `${API}/client-locator/client/${item.type}/${encodeURIComponent(item.id)}/jobs`,
        { params: { date: format(d, 'yyyy-MM-dd') } },
      );
      setJobs(r.data?.jobs || []);
    } catch (e) {
      setJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  // initial load + react to date changes
  useEffect(() => {
    fetchSites(date);
    if (selected) fetchJobs(selected, date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStr]);

  // ── unified search (debounced) ──────────────────────────────────────────────
  useEffect(() => {
    const q = query.trim();
    if (!q) { 
      setResults(allCommercial); 
      return; 
    }
    if (selected && q === (selected.name || '')) return; // don't re-search the picked name
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(`${API}/client-locator/search`, { params: { q, limit: 10000 } });
        setResults(r.data?.results || []);
        setShowResults(true);
      } catch (e) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, allCommercial]);

  // close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowResults(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // ── selection ────────────────────────────────────────────────────────────
  const selectClient = useCallback((item, { moveMap = true } = {}) => {
    setSelected(item);
    setQuery(item.name || '');
    setShowResults(false);
    setResults([]);
    fetchJobs(item, date);
    if (moveMap) {
      const own = sites.filter((s) => String(s.client_id) === String(item.id) && s.type === item.type);
      if (own.length) fitToSites(own);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites, date, fetchJobs, fitToSites]);

  const onPinClick = (site) => {
    setSelected({
      type: site.type, id: String(site.client_id), name: site.client_name,
      sub: site.site_name, mapped: true,
    });
    setQuery(site.client_name || '');
    setShowResults(false);
    setTarget({ type: 'point', center: [site.latitude, site.longitude], zoom: 14, token: Date.now() });
    fetchJobs({ type: site.type, id: String(site.client_id) }, date);
  };

  const clearSelection = () => {
    setSelected(null);
    setJobs([]);
    setQuery('');
  };

  // ── geographic tiers ────────────────────────────────────────────────────────
  const regions = useMemo(
    () => Array.from(new Set(sites.map((s) => s.state).filter(Boolean))).sort(),
    [sites],
  );
  const cities = useMemo(
    () => Array.from(new Set(
      sites.filter((s) => !activeRegion || s.state === activeRegion).map((s) => s.city).filter(Boolean),
    )).sort(),
    [sites, activeRegion],
  );

  const visibleSites = useMemo(() => sites.filter((s) => {
    if (activeRegion && s.state !== activeRegion) return false;
    if (activeCity && s.city !== activeCity) return false;
    if (clientTypeFilter !== 'all' && s.type !== clientTypeFilter) return false;
    return true;
  }), [sites, activeRegion, activeCity, clientTypeFilter]);

  const handleShowAll = () => {
    setActiveRegion(null);
    setActiveCity(null);
    setClientTypeFilter('all');
    setQuery('');
    setSelected(null);
    fitToSites(sites);
  };

  const switchTier = (key) => {
    setTier(key);
    if (key === 'country') {
      setActiveRegion(null); setActiveCity(null);
      fitToSites(sites);
    } else if (key === 'region') {
      setActiveCity(null);
      if (!activeRegion) fitToSites(sites);
    }
  };
  const selectRegion = (r) => {
    setActiveRegion(r); setActiveCity(null);
    fitToSites(sites.filter((s) => s.state === r));
  };
  const selectCity = (c) => {
    const citySt0 = sites.find((s) => s.city === c);
    if (citySt0) setActiveRegion(citySt0.state);
    setActiveCity(c);
    fitToSites(sites.filter((s) => s.city === c));
  };

  const fmtTime = (t) => (t ? format(new Date(t), 'h:mm a') : null);

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="cl-root" style={{ background: `radial-gradient(1200px 600px at 80% -10%, rgba(99,102,241,0.18), transparent), linear-gradient(180deg, ${C.bg0}, ${C.bg1})`, minHeight: '100vh', color: C.text, padding: 22 }}>
      <style>{CL_CSS}</style>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 220, damping: 22 }}
        className="cl-header"
      >
        <div>
          <div className="cl-title">
            <span className="cl-title-badge"><Navigation size={18} /></span>
            Client Locator
          </div>
          <div className="cl-subtitle">
            Find any client on the map or by search, then pick a date to see their job in any state.
          </div>
        </div>

        <div className="cl-header-controls">
        <button className="cl-tier" onClick={handleShowAll} style={{ background: C.blue, color: '#fff', border: 'none' }}>
          <MapIcon size={15} /> Show All
        </button>
        
        <select 
          value={clientTypeFilter} 
          onChange={e => setClientTypeFilter(e.target.value)}
          className="cl-date-btn"
          style={{ padding: '6px 12px', background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, outline: 'none' }}
        >
          <option value="all">All Clients</option>
          <option value="commercial">Commercial</option>
          <option value="residential">Residential</option>
        </select>

          {/* View tier */}
          <div className="cl-tiers">
            {TIERS.map(({ key, label, Icon }) => (
              <button
                key={key}
                className={`cl-tier ${tier === key ? 'is-active' : ''}`}
                onClick={() => switchTier(key)}
              >
                {tier === key && <motion.span layoutId="tierPill" className="cl-tier-pill" transition={{ type: 'spring', stiffness: 380, damping: 30 }} />}
                <span className="cl-tier-inner"><Icon size={15} />{label}</span>
              </button>
            ))}
          </div>

          {/* Date */}
          <DatePicker
            selected={date}
            onChange={(d) => d && setDate(d)}
            dateFormat="MMM d, yyyy"
            customInput={<DateInput />}
            calendarClassName="cl-cal"
            popperPlacement="bottom-end"
          />
        </div>
      </motion.div>

      {/* Body */}
      <div className="cl-body">
        {/* ── Map ─────────────────────────────────────────────── */}
        <motion.div className="cl-map-wrap" variants={rise} initial="hidden" animate="show">
          <MapContainer
            center={[25, 50]}
            zoom={3}
            scrollWheelZoom
            zoomControl={false}
            style={{ height: '100%', width: '100%' }}
            worldCopyJump
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              attribution='&copy; OpenStreetMap &copy; CARTO'
            />
            <MapController target={target} />

            {visibleSites.map((s, index) => {
              const info = getSiteInfo(s);
              const isActive = selected && String(selected.id) === String(s.client_id) && selected.type === s.type;
              
              // Offset duplicates so they don't perfectly overlap
              const duplicatesBefore = visibleSites.slice(0, index).filter(
                (prev) => prev.latitude === s.latitude && prev.longitude === s.longitude
              ).length;
              const offset = duplicatesBefore * 0.00005; // small offset
              
              return (
                <Marker
                  key={s.site_id}
                  position={[s.latitude + offset, s.longitude + offset]}
                  icon={pinIcon(info.color, isActive, s.type)}
                  eventHandlers={{ click: () => onPinClick(s) }}
                >
                  <Popup>
                    <div className="cl-popup">
                      <div className="cl-popup-name">{s.client_name}</div>
                      <div className="cl-popup-site">{s.site_name}</div>
                      <div className="cl-popup-loc">{[s.city, s.state].filter(Boolean).join(', ')}</div>
                      <div className="cl-popup-status" style={{ '--c': info.color }}>
                        <span className="cl-dot" /> {info.label}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>

          {/* Map overlays */}
          <div className="cl-map-counter">
            {loadingSites
              ? <><Loader2 size={13} className="cl-spin" /> loading…</>
              : <><MapPin size={13} /> {visibleSites.length} site{visibleSites.length === 1 ? '' : 's'} plotted</>}
          </div>

          <div className="cl-legend">
            {[
              ['Scheduled', '#38BDF8'], ['In progress', '#F59E0B'],
              ['Completed', '#34D399'], ['Cancelled', '#F87171'], ['No job', '#64748B'],
            ].map(([label, color]) => (
              <span key={label} className="cl-legend-item">
                <span className="cl-legend-dot" style={{ background: color }} /> {label}
              </span>
            ))}
          </div>

          {/* Region / City chips */}
          <AnimatePresence>
            {(tier === 'region' || tier === 'city') && (
              <motion.div
                className="cl-chips"
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 14 }}
              >
                {(tier === 'region' ? regions : cities).length === 0 && (
                  <span className="cl-chips-empty">No {tier === 'region' ? 'regions' : 'cities'} available</span>
                )}
                {(tier === 'region' ? regions : cities).map((v) => {
                  const active = tier === 'region' ? activeRegion === v : activeCity === v;
                  return (
                    <button
                      key={v}
                      className={`cl-chip ${active ? 'is-active' : ''}`}
                      onClick={() => (tier === 'region' ? selectRegion(v) : selectCity(v))}
                    >
                      <Crosshair size={12} /> {v}
                    </button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* ── Side panel ──────────────────────────────────────── */}
        <motion.div className="cl-panel" variants={rise} initial="hidden" animate="show">
          {/* Search */}
          <div className="cl-search" ref={searchBoxRef}>
            <div className="cl-search-field">
              {searching ? <Loader2 size={17} className="cl-spin" /> : <Search size={17} />}
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => (results.length || allCommercial.length) && setShowResults(true)}
                placeholder="Search clients, contacts or phone…"
              />
              {query && (
                <button className="cl-search-clear" onClick={clearSelection} aria-label="Clear">
                  <X size={15} />
                </button>
              )}
            </div>

            <AnimatePresence>
              {showResults && results.length > 0 && (
                <motion.div
                  className="cl-results"
                  initial={{ opacity: 0, y: -8, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -8, height: 0 }}
                >
                  {results.map((r, i) => (
                    <motion.button
                      key={`${r.type}-${r.id}`}
                      className="cl-result"
                      onClick={() => selectClient(r)}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.025 }}
                    >
                      <span className={`cl-result-icon ${r.type}`}>
                        {r.type === 'commercial' ? <Building2 size={15} /> : <Home size={15} />}
                      </span>
                      <span className="cl-result-main">
                        <span className="cl-result-name">{r.name || 'Unnamed'}</span>
                        <span className="cl-result-sub">
                          {[r.contact, r.phone, [r.city, r.state].filter(Boolean).join(', ')]
                            .filter(Boolean).join(' · ') || (r.type === 'commercial' ? `${r.site_count} site${r.site_count === 1 ? '' : 's'}` : '—')}
                        </span>
                      </span>
                      <span className={`cl-tag ${r.type}`}>{r.type === 'commercial' ? 'C&I' : 'Resi'}</span>
                      <ChevronRight size={15} className="cl-result-chev" />
                    </motion.button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Result area */}
          <div className="cl-result-area">
            <AnimatePresence mode="wait">
              {!selected ? (
                <motion.div
                  key="empty-initial"
                  className="cl-placeholder"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                >
                  <motion.div
                    className="cl-placeholder-icon"
                    animate={{ y: [0, -8, 0] }}
                    transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
                  >
                    <Navigation size={30} />
                  </motion.div>
                  <div className="cl-placeholder-title">Pick a client</div>
                  <div className="cl-placeholder-text">
                    Tap a pin on the map or search above, then choose a date to reveal the job.
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key={`${selected.type}-${selected.id}`}
                  initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  transition={{ type: 'spring', stiffness: 240, damping: 24 }}
                >
                  {/* Selected client header */}
                  <div className="cl-selected">
                    <span className={`cl-result-icon ${selected.type}`}>
                      {selected.type === 'commercial' ? <Building2 size={16} /> : <Home size={16} />}
                    </span>
                    <div className="cl-selected-main">
                      <div className="cl-selected-name">{selected.name}</div>
                      <div className="cl-selected-sub">
                        {selected.sub ? `${selected.sub} · ` : ''}
                        {selected.type === 'commercial' ? 'Commercial / Industrial' : 'Residential'}
                      </div>
                    </div>
                    <button className="cl-selected-x" onClick={clearSelection}><X size={16} /></button>
                  </div>

                  <div className="cl-onday">
                    <CalendarDays size={14} /> Jobs on <strong>{format(date, 'EEE, MMM d, yyyy')}</strong>
                  </div>

                  {selected.type === 'residential' && !selected.mapped && (
                    <div className="cl-note"><MapPin size={13} /> No map location on file for this customer.</div>
                  )}

                  {/* Jobs */}
                  {loadingJobs ? (
                    <div className="cl-loading"><Loader2 size={22} className="cl-spin" /> Loading jobs…</div>
                  ) : jobs.length === 0 ? (
                    <motion.div className="cl-nojobs" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}>
                      <Inbox size={26} />
                      <div className="cl-nojobs-title">No job on this day</div>
                      <div className="cl-nojobs-text">Try another date for {selected.name}.</div>
                    </motion.div>
                  ) : (
                    <motion.div initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.07 } } }}>
                      <AnimatePresence>
                        {jobs.map((job, i) => (
                          <JobCard key={job.schedule_id || job.job_id || i} job={job} type={selected.type} fmtTime={fmtTime} />
                        ))}
                      </AnimatePresence>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// ── Job card ──────────────────────────────────────────────────────────────
function JobCard({ job, type, fmtTime }) {
  const info = getSiteInfo({ job_status: job.status });
  const start = fmtTime(job.started_at);
  const end = fmtTime(job.completed_at);

  return (
    <motion.div
      className="cl-job"
      variants={rise}
      layout
      style={{ '--c': info.color }}
      whileHover={{ y: -3 }}
    >
      <div className="cl-job-bar" />
      <div className="cl-job-head">
        <span className="cl-status" style={{ '--c': info.color }}>
          <span className="cl-dot" /> {info.label}
        </span>
        {job.shift && <span className="cl-job-shift">{job.shift}</span>}
      </div>

      <div className="cl-job-title">
        {type === 'commercial'
          ? (job.site_name || 'Site')
          : (job.service_type || 'Service')}
      </div>

      <div className="cl-job-meta">
        {type === 'commercial' ? (
          <>
            {(job.city || job.state) && (
              <span><MapPin size={13} /> {[job.city, job.state].filter(Boolean).join(', ')}</span>
            )}
            {job.team_lead_name && <span><Users size={13} /> {job.team_lead_name}</span>}
            {job.estimated_duration && <span><Clock size={13} /> ~{job.estimated_duration} min</span>}
            {job.approval_status && <span><Briefcase size={13} /> {job.approval_status}</span>}
          </>
        ) : (
          <>
            {(job.city || job.state) && (
              <span><MapPin size={13} /> {[job.city, job.state].filter(Boolean).join(', ')}</span>
            )}
            {job.assigned_team && <span><Users size={13} /> {job.assigned_team}</span>}
            {job.system_size && <span><Briefcase size={13} /> {job.system_size}</span>}
            {job.phone && <span><Phone size={13} /> {job.phone}</span>}
          </>
        )}
      </div>

      {(start || end) && (
        <div className="cl-job-times">
          {start && <span><Clock size={12} /> Start {start}</span>}
          {end && <span><Clock size={12} /> End {end}</span>}
        </div>
      )}

      {job.notes && <div className="cl-job-notes">{job.notes}</div>}
    </motion.div>
  );
}

// ── Scoped styles ───────────────────────────────────────────────────────────
const CL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=DM+Mono:wght@400;500&display=swap');

.cl-root, .cl-root * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; }
.cl-root { --accent:${C.accent}; }

/* Header */
.cl-header { display:flex; justify-content:space-between; align-items:flex-end; gap:20px; flex-wrap:wrap; margin-bottom:18px; }
.cl-title { display:flex; align-items:center; gap:12px; font-size:26px; font-weight:700; letter-spacing:-0.02em; }
.cl-title-badge { display:grid; place-items:center; width:38px; height:38px; border-radius:11px;
  background:linear-gradient(135deg, ${C.accent}, ${C.accent2}); color:#04121f;
  box-shadow:0 8px 26px rgba(56,189,248,0.35); }
.cl-subtitle { color:${C.muted}; font-size:13.5px; margin-top:6px; max-width:560px; }
.cl-header-controls { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }

/* Tier segmented control */
.cl-tiers { display:flex; gap:4px; padding:4px; border-radius:13px; background:${C.surface}; border:1px solid ${C.border}; }
.cl-tier { position:relative; border:none; background:transparent; color:${C.muted}; cursor:pointer;
  padding:8px 14px; border-radius:9px; font-size:13px; font-weight:600; }
.cl-tier.is-active { color:#04121f; }
.cl-tier-inner { position:relative; z-index:1; display:inline-flex; align-items:center; gap:7px; }
.cl-tier-pill { position:absolute; inset:0; border-radius:9px;
  background:linear-gradient(135deg, ${C.accent}, #7dd3fc); box-shadow:0 6px 18px rgba(56,189,248,0.4); }

/* Date button */
.cl-date-btn { display:inline-flex; align-items:center; gap:9px; cursor:pointer;
  background:${C.surface}; border:1px solid ${C.border}; color:${C.text};
  padding:10px 16px; border-radius:13px; font-size:13.5px; font-weight:600; font-family:'DM Mono', monospace; }
.cl-date-btn:hover { border-color:${C.borderHi}; }

/* Body grid */
.cl-body { display:grid; grid-template-columns: 1fr 400px; gap:18px; height:76vh; min-height:560px; }
@media (max-width: 980px){ .cl-body { grid-template-columns:1fr; height:auto; } .cl-map-wrap{ height:56vh; } .cl-panel{ height:auto; } }

/* Map */
.cl-map-wrap { position:relative; border-radius:18px; overflow:hidden; border:1px solid ${C.border};
  box-shadow:0 24px 60px rgba(0,0,0,0.45); }
.cl-map-wrap .leaflet-container { background:#0a0f1a; }
.cl-map-counter { position:absolute; top:14px; left:14px; z-index:600; display:flex; align-items:center; gap:7px;
  background:rgba(11,17,32,0.82); backdrop-filter:blur(8px); border:1px solid ${C.border};
  color:${C.accent}; padding:7px 13px; border-radius:20px; font-size:11.5px; font-weight:700;
  font-family:'DM Mono', monospace; letter-spacing:0.03em; }
.cl-legend { position:absolute; bottom:14px; left:14px; z-index:600; display:flex; flex-wrap:wrap; gap:10px;
  background:rgba(11,17,32,0.82); backdrop-filter:blur(8px); border:1px solid ${C.border};
  padding:9px 13px; border-radius:13px; max-width:60%; }
.cl-legend-item { display:inline-flex; align-items:center; gap:6px; font-size:11px; color:${C.muted}; font-weight:500; }
.cl-legend-dot { width:9px; height:9px; border-radius:50%; box-shadow:0 0 8px currentColor; }

/* Region / city chips */
.cl-chips { position:absolute; top:14px; right:14px; z-index:600; display:flex; flex-direction:column;
  align-items:flex-end; gap:7px; max-height:calc(100% - 28px); overflow:auto; max-width:240px; padding-left:8px; }
.cl-chip { display:inline-flex; align-items:center; gap:6px; cursor:pointer; white-space:nowrap;
  background:rgba(11,17,32,0.85); backdrop-filter:blur(8px); border:1px solid ${C.border}; color:${C.text};
  padding:7px 12px; border-radius:20px; font-size:12px; font-weight:600; transition:all .15s; }
.cl-chip:hover { border-color:${C.borderHi}; transform:translateX(-2px); }
.cl-chip.is-active { background:linear-gradient(135deg, ${C.accent}, ${C.accent2}); color:#04121f; border-color:transparent; }
.cl-chips-empty { font-size:11.5px; color:${C.faint}; background:rgba(11,17,32,0.8); padding:6px 12px; border-radius:14px; }

/* Leaflet pins */
.cl-pin-wrap { background:transparent !important; border:none !important; }
.cl-pin { display:block; width:14px; height:14px; background:var(--pin);
  border:2px solid #0B1120; box-shadow:0 0 0 2px var(--pin), 0 0 14px var(--pin); position:relative; cursor:pointer; }
.cl-pin-commercial { border-radius:4px; }
.cl-pin-residential { border-radius:50%; }
.cl-pin::after { content:''; position:absolute; inset:-4px; border:inherit; border-radius:inherit; border:2px solid var(--pin);
  opacity:.6; animation:clPulse 2.1s ease-out infinite; }
.cl-pin-active { width:20px; height:20px; }
.cl-pin-active::after { animation-duration:1.2s; }
@keyframes clPulse { 0%{ transform:scale(.55); opacity:.75 } 100%{ transform:scale(2.4); opacity:0 } }

/* Leaflet popup dark theme */
.cl-root .leaflet-popup-content-wrapper { background:${C.bg1}; color:${C.text}; border:1px solid ${C.border};
  border-radius:12px; box-shadow:0 18px 40px rgba(0,0,0,0.5); }
.cl-root .leaflet-popup-tip { background:${C.bg1}; }
.cl-root .leaflet-popup-content { margin:11px 14px; }
.cl-popup-name { font-weight:700; font-size:13.5px; }
.cl-popup-site { font-size:12px; color:${C.muted}; margin-top:1px; }
.cl-popup-loc { font-size:11px; color:${C.faint}; margin-top:2px; }
.cl-popup-status { display:inline-flex; align-items:center; gap:6px; margin-top:7px; font-size:11.5px; font-weight:700; color:var(--c); }

/* Panel */
.cl-panel { display:flex; flex-direction:column; gap:14px; height:100%; min-height:0; }
.cl-search { position:relative; }
.cl-search-field { display:flex; align-items:center; gap:10px; background:${C.surface}; border:1px solid ${C.border};
  border-radius:14px; padding:12px 14px; transition:border-color .15s, box-shadow .15s; }
.cl-search-field:focus-within { border-color:${C.borderHi}; box-shadow:0 0 0 3px rgba(56,189,248,0.12); }
.cl-search-field svg { color:${C.muted}; flex:none; }
.cl-search-field input { flex:1; background:transparent; border:none; outline:none; color:${C.text}; font-size:14px; }
.cl-search-field input::placeholder { color:${C.faint}; }
.cl-search-clear { background:transparent; border:none; color:${C.muted}; cursor:pointer; display:grid; place-items:center; padding:2px; }
.cl-search-clear:hover { color:${C.text}; }

.cl-results { position:absolute; top:calc(100% + 8px); left:0; right:0; z-index:700; overflow:hidden;
  background:${C.bg1}; border:1px solid ${C.border}; border-radius:14px; box-shadow:0 24px 50px rgba(0,0,0,0.55);
  max-height:340px; overflow-y:auto; }
.cl-result { display:flex; align-items:center; gap:11px; width:100%; text-align:left; cursor:pointer;
  background:transparent; border:none; border-bottom:1px solid ${C.border}; padding:11px 13px; color:${C.text}; }
.cl-result:last-child { border-bottom:none; }
.cl-result:hover { background:${C.surface2}; }
.cl-result-icon { display:grid; place-items:center; width:32px; height:32px; border-radius:9px; flex:none; }
.cl-result-icon.commercial { background:rgba(56,189,248,0.14); color:${C.accent}; }
.cl-result-icon.residential { background:rgba(52,211,153,0.14); color:#34D399; }
.cl-result-main { flex:1; min-width:0; }
.cl-result-name { display:block; font-weight:600; font-size:13.5px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.cl-result-sub { display:block; font-size:11.5px; color:${C.muted}; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.cl-tag { font-size:10px; font-weight:700; padding:3px 8px; border-radius:7px; flex:none; letter-spacing:0.03em; }
.cl-tag.commercial { background:rgba(56,189,248,0.16); color:${C.accent}; }
.cl-tag.residential { background:rgba(52,211,153,0.16); color:#34D399; }
.cl-result-chev { color:${C.faint}; flex:none; }

/* Result area */
.cl-result-area { flex:1; min-height:0; overflow-y:auto; padding-right:2px; }
.cl-placeholder { display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center;
  height:100%; min-height:320px; gap:10px; }
.cl-placeholder-icon { display:grid; place-items:center; width:72px; height:72px; border-radius:50%;
  background:${C.surface}; border:1px solid ${C.border}; color:${C.accent}; }
.cl-placeholder-title { font-size:17px; font-weight:700; }
.cl-placeholder-text { font-size:13px; color:${C.muted}; max-width:260px; }

.cl-selected { display:flex; align-items:center; gap:11px; background:${C.surface}; border:1px solid ${C.border};
  border-radius:14px; padding:12px 13px; }
.cl-selected-main { flex:1; min-width:0; }
.cl-selected-name { font-weight:700; font-size:15px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.cl-selected-sub { font-size:11.5px; color:${C.muted}; }
.cl-selected-x { background:transparent; border:none; color:${C.muted}; cursor:pointer; display:grid; place-items:center; }
.cl-selected-x:hover { color:${C.text}; }

.cl-onday { display:flex; align-items:center; gap:7px; font-size:12.5px; color:${C.muted}; margin:14px 2px 10px; }
.cl-onday strong { color:${C.text}; font-weight:600; }
.cl-note { display:flex; align-items:center; gap:7px; font-size:12px; color:#FBBF24;
  background:rgba(251,191,36,0.1); border:1px solid rgba(251,191,36,0.22); padding:8px 11px; border-radius:10px; margin-bottom:12px; }

.cl-loading, .cl-nojobs { display:flex; flex-direction:column; align-items:center; justify-content:center; gap:9px;
  text-align:center; color:${C.muted}; padding:40px 10px; }
.cl-nojobs svg { color:${C.faint}; }
.cl-nojobs-title { font-size:15px; font-weight:700; color:${C.text}; }
.cl-nojobs-text { font-size:12.5px; }

/* Job card */
.cl-job { position:relative; overflow:hidden; background:${C.surface}; border:1px solid ${C.border};
  border-radius:14px; padding:14px 16px 14px 18px; margin-bottom:11px; }
.cl-job:hover { border-color:rgba(255,255,255,0.16); }
.cl-job-bar { position:absolute; left:0; top:0; bottom:0; width:4px; background:var(--c); box-shadow:0 0 14px var(--c); }
.cl-job-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; }
.cl-status { display:inline-flex; align-items:center; gap:7px; font-size:12px; font-weight:700; color:var(--c); }
.cl-dot { width:8px; height:8px; border-radius:50%; background:var(--c); box-shadow:0 0 8px var(--c); }
.cl-job-shift { font-size:10.5px; font-weight:600; color:${C.muted}; text-transform:capitalize;
  background:${C.surface2}; padding:3px 9px; border-radius:7px; }
.cl-job-title { font-size:15px; font-weight:700; margin-bottom:9px; }
.cl-job-meta { display:flex; flex-wrap:wrap; gap:8px 14px; }
.cl-job-meta span { display:inline-flex; align-items:center; gap:6px; font-size:12px; color:${C.muted}; }
.cl-job-meta svg { color:${C.faint}; }
.cl-job-times { display:flex; gap:14px; margin-top:10px; padding-top:10px; border-top:1px solid ${C.border}; }
.cl-job-times span { display:inline-flex; align-items:center; gap:6px; font-size:11.5px; color:${C.muted}; font-family:'DM Mono', monospace; }
.cl-job-notes { margin-top:9px; font-size:12px; color:${C.muted}; line-height:1.5;
  background:rgba(255,255,255,0.03); border-radius:9px; padding:8px 10px; }

/* utils */
.cl-spin { animation:clSpin 0.9s linear infinite; }
@keyframes clSpin { to { transform:rotate(360deg); } }
.cl-result-area::-webkit-scrollbar, .cl-results::-webkit-scrollbar, .cl-chips::-webkit-scrollbar { width:7px; }
.cl-result-area::-webkit-scrollbar-thumb, .cl-results::-webkit-scrollbar-thumb, .cl-chips::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.13); border-radius:7px; }

/* react-datepicker dark theme */
.cl-cal.react-datepicker { background:${C.bg1}; border:1px solid ${C.border}; border-radius:14px; font-family:'DM Sans', sans-serif;
  box-shadow:0 24px 50px rgba(0,0,0,0.55); overflow:hidden; color:${C.text}; }
.cl-cal .react-datepicker__header { background:${C.surface}; border-bottom:1px solid ${C.border}; padding-top:12px; }
.cl-cal .react-datepicker__current-month, .cl-cal .react-datepicker__day-name { color:${C.text}; }
.cl-cal .react-datepicker__day { color:${C.muted}; border-radius:8px; }
.cl-cal .react-datepicker__day:hover { background:${C.surface2}; color:${C.text}; }
.cl-cal .react-datepicker__day--selected, .cl-cal .react-datepicker__day--keyboard-selected {
  background:linear-gradient(135deg, ${C.accent}, ${C.accent2}); color:#04121f; font-weight:700; }
.cl-cal .react-datepicker__day--outside-month { color:${C.faint}; opacity:.5; }
.cl-cal .react-datepicker__triangle { display:none; }
.cl-cal .react-datepicker__navigation-icon::before { border-color:${C.muted}; }
`;
